import React, { useEffect, useState } from 'react'
import { Button, Box, Typography, Alert, CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import axios from 'axios'

const MFASettings = () => {
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    fetchMfaSettings()
  }, [])

  const fetchMfaSettings = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/settings/mfa')
      setMfaEnabled(response.data.mfaEnabled)
    } catch (error) {
      console.error('Error fetching MFA settings:', error)
      toast.error('Failed to fetch MFA settings')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleMFA = async () => {
    try {
      setUpdating(true)
      const newMfaStatus = !mfaEnabled
      
      await axios.patch('/admins/tenant-config', {
        mfaEnabled: newMfaStatus
      })
      
      setMfaEnabled(newMfaStatus)
      toast.success(`MFA ${newMfaStatus ? 'enabled' : 'disabled'} successfully`)
    } catch (error) {
      console.error('Error updating MFA settings:', error)
      toast.error('Failed to update MFA settings')
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Multi-Factor Authentication Settings
      </Typography>
      
      <Alert 
        severity={mfaEnabled ? "success" : "info"} 
        sx={{ mb: 3 }}
      >
        {mfaEnabled 
          ? "Multi-factor authentication is currently enabled for your organization."
          : "Multi-factor authentication is currently disabled for your organization."
        }
      </Alert>

      <Typography variant="body1" paragraph>
        {mfaEnabled 
          ? "When MFA is enabled, all users in your organization will be required to use multi-factor authentication for enhanced security."
          : "Enable multi-factor authentication to add an extra layer of security for all users in your organization."
        }
      </Typography>

      <Box mt={3}>
        <Button
          variant="contained"
          color={mfaEnabled ? "error" : "primary"}
          onClick={handleToggleMFA}
          disabled={updating}
          size="large"
        >
          {updating ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            mfaEnabled ? "Disable MFA" : "Enable MFA"
          )}
        </Button>
      </Box>

      {mfaEnabled && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Disabling MFA will remove the requirement for all users in your organization. 
            Users who have already set up MFA will have their MFA disabled automatically.
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

export default MFASettings 